import React, { useState } from "react";
import { Trans, useTranslation } from "react-i18next";
import HeaderTS from "../global/headerTS";
import Footer2 from "../global/footerv2";
import SectionTitle from "../section-title";
import Ctav3 from "../ctav3";
import aiData from "../../data/ai-chatbot.json";

// every screenshot is captured twice; the Chinese UI copy carries a -tc suffix
const localizedImage = (src, lng) =>
  lng && lng.startsWith("tc") ? src.replace(/\.webp$/, "-tc.webp") : src;

const ContactButtons = ({ hero, i18nPrefix }) => (
  <div className="d-flex flex-wrap align-items-center justify-content-center justify-content-sm-start gap-3">
    <a
      rel="noreferrer"
      href={`tel:${hero.contactPhone}`}
      className="btn__primary2 btn-md smooth"
    >
      <span>
        <i className="icofont-phone icofont-1x"></i>
        &nbsp;&nbsp;&nbsp;
        <Trans i18nKey={`${i18nPrefix}.phone`}>Phone</Trans>
      </span>
    </a>
    <a
      rel="noreferrer"
      href={`https://wa.me/${hero.contactWhatsApp.replace(/[\s+]/g, "")}`}
      className="btn__primary2 btn-md smooth"
      target="_blank"
    >
      <span>
        <i className="icofont-brand-whatsapp icofont-1x"></i>
        &nbsp;&nbsp;&nbsp;
        <Trans i18nKey={`${i18nPrefix}.whatsapp`}>WhatsApp Us</Trans>
      </span>
    </a>
    <a
      rel="noreferrer"
      href={`mailto:${hero.contactEmail}`}
      className="btn__primary2 btn-md smooth"
    >
      <span>
        <i className="icofont-email icofont-1x"></i>
        &nbsp;&nbsp;&nbsp;
        <Trans i18nKey={`${i18nPrefix}.email`}>eMail</Trans>
      </span>
    </a>
  </div>
);

// tabbed screenshot in a browser-style frame, used by the How It Works sections
const ShotTabs = ({ screens, i18nPrefix, lng }) => {
  const [activeId, setActiveId] = useState(screens[0].id);
  const shot = screens.find((s) => s.id === activeId) || screens[0];
  const src = localizedImage(shot.image, lng);
  return (
    <div className="tcm-shot ai-shot">
      {screens.length > 1 && (
        <div className="erp-screens__tabs tcm-shot__tabs" role="tablist">
          {screens.map((s) => (
            <button
              key={s.id}
              type="button"
              role="tab"
              aria-selected={s.id === shot.id}
              className={`erp-screens__tab ${s.id === shot.id ? "is-active" : ""}`}
              onClick={() => setActiveId(s.id)}
            >
              <i className={s.icon}></i>
              <Trans i18nKey={`${i18nPrefix}.screen${s.id}.title`}>
                {s.title}
              </Trans>
            </button>
          ))}
        </div>
      )}
      <div className="erp-screens__frame tcm-shot__frame" role="tabpanel">
        <div className="erp-screens__frame--bar">
          <span></span>
          <span></span>
          <span></span>
        </div>
        <a href={src} target="_blank" rel="noreferrer">
          <img key={src} className="img-fluid" src={src} alt={shot.title} />
        </a>
      </div>
      <p className="tcm-shot__caption">
        <Trans i18nKey={`${i18nPrefix}.screen${shot.id}.description`}>
          {shot.description}
        </Trans>
      </p>
    </div>
  );
};

// icon + title + text rows, the about2 list from the Reboot template
const ItemList = ({ items, i18nPrefix, numbered }) => (
  <div className="about2__items tcm-items">
    {items.map((item) => (
      <div key={item.id} className="about2__items--single tcm-items__single">
        <div className="tcm-items__icon">
          {numbered ? <span>{item.id}</span> : <i className={item.icon}></i>}
        </div>
        <div className="about2__items--text">
          <h3>
            <Trans i18nKey={`${i18nPrefix}.item${item.id}.title`}>
              {item.title}
            </Trans>
          </h3>
          <p>
            <Trans i18nKey={`${i18nPrefix}.item${item.id}.description`}>
              {item.description}
            </Trans>
          </p>
        </div>
      </div>
    ))}
  </div>
);

const AiChatbot = ({ header }) => {
  const {
    aiHero,
    aiFeatures,
    aiNotify,
    aiIntegrate,
    aiLanguage,
    aiPersona,
    aiScreens,
    aiPricing,
  } = aiData;
  const { i18n } = useTranslation();
  const lng = i18n.language;

  const [activeScreen, setActiveScreen] = useState(aiScreens.screens[0].id);
  const screen =
    aiScreens.screens.find((s) => s.id === activeScreen) ||
    aiScreens.screens[0];
  const screenSrc = localizedImage(screen.image, lng);
  const integration = aiPricing.integration;

  return (
    <>
      <HeaderTS header={header} />

      {/* Hero Section */}
      <section id="hero" className="hero3 hero-padding bg-one tcm-hero ai-hero">
        <div className="container">
          <div className="row align-items-center gy-5">
            <div className="col-lg-6 hero3__content">
              <p className="badge-text">
                <Trans i18nKey="aiChatbot.hero.subtitle">{aiHero.subtitle}</Trans>
              </p>
              <h1 className="display-5 mb-4 hero3__title">
                <Trans i18nKey="aiChatbot.hero.title">{aiHero.title}</Trans>
              </h1>
              <p className="fs-5 mb-4">
                <Trans i18nKey="aiChatbot.hero.description">
                  {aiHero.description}
                </Trans>
              </p>
              <p className="ai-hero__price">
                <i className="icofont-tag"></i>
                <Trans i18nKey="aiChatbot.hero.price">{aiHero.price}</Trans>
              </p>
              <ContactButtons hero={aiHero} i18nPrefix="aiChatbot.hero" />
            </div>

            <div className="col-lg-6">
              <div className="tcm-hero__stack">
                <div className="tcm-hero__window tcm-hero__window--back">
                  <div className="erp-screens__frame--bar">
                    <span></span>
                    <span></span>
                    <span></span>
                  </div>
                  <img
                    src={localizedImage(aiHero.imageBack, lng)}
                    alt={aiHero.subtitle}
                  />
                </div>
                <div className="tcm-hero__window tcm-hero__window--front">
                  <div className="erp-screens__frame--bar">
                    <span></span>
                    <span></span>
                    <span></span>
                  </div>
                  <img
                    src={localizedImage(aiHero.imageFront, lng)}
                    alt={aiHero.subtitle}
                  />
                </div>
                <ul className="tcm-hero__chips">
                  {aiHero.chips.map((chip) => (
                    <li key={chip.id}>
                      <i className={chip.icon}></i>
                      <Trans i18nKey={`aiChatbot.hero.chip${chip.id}`}>
                        {chip.title}
                      </Trans>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Key Features Section */}
      <section id="features" className="service section-padding ai-features">
        <div className="container">
          <SectionTitle
            subtitle={
              <Trans i18nKey="aiChatbot.features.subtitle">
                {aiFeatures.subtitle}
              </Trans>
            }
            title={
              <Trans i18nKey="aiChatbot.features.title">{aiFeatures.title}</Trans>
            }
            direction="center"
          />
          <ul className="ai-legend">
            <li className="ai-legend__item ai-legend__item--basic">
              <Trans i18nKey="aiChatbot.features.basic">{aiFeatures.basic}</Trans>
            </li>
            <li className="ai-legend__item ai-legend__item--integration">
              <Trans i18nKey="aiChatbot.features.integration">
                {aiFeatures.integration}
              </Trans>
            </li>
          </ul>
          <div className="row pt-4 g-4">
            {aiFeatures.features.map((feature) => (
              <div key={feature.id} className="col-md-6 col-xl-3">
                <div
                  className={`ai-feature ai-feature--${feature.type} translateEffect1 h-100`}
                >
                  <div className="ai-feature__head">
                    <span className="ai-feature__icon">
                      <i className={feature.icon}></i>
                    </span>
                    <h4>
                      <Trans i18nKey={`aiChatbot.features.feature${feature.id}.title`}>
                        {feature.title}
                      </Trans>
                    </h4>
                  </div>
                  <p>
                    <Trans
                      i18nKey={`aiChatbot.features.feature${feature.id}.description`}
                    >
                      {feature.description}
                    </Trans>
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* How It Works 1: WA Notification */}
      <section id="notify" className="section-padding erp-flow bg-one ai-notify">
        <div className="container">
          <SectionTitle
            subtitle={
              <Trans i18nKey="aiChatbot.notify.subtitle">{aiNotify.subtitle}</Trans>
            }
            title={<Trans i18nKey="aiChatbot.notify.title">{aiNotify.title}</Trans>}
            direction="center"
          />
          <ol className="erp-flow__ribbon ai-ribbon">
            {aiNotify.steps.map((step, i) => (
              <li
                key={step.id}
                // earlier panels paint over the notch of the next one
                style={{ zIndex: aiNotify.steps.length - i }}
                className={`erp-flow__ribbon--step${
                  step.highlight ? " is-final" : ""
                }`}
              >
                <i className={step.icon}></i>
                <h5>
                  <Trans i18nKey={`aiChatbot.notify.step${step.id}.title`}>
                    {step.title}
                  </Trans>
                </h5>
                <p className="ai-ribbon__text">
                  <Trans i18nKey={`aiChatbot.notify.step${step.id}.description`}>
                    {step.description}
                  </Trans>
                </p>
              </li>
            ))}
          </ol>

          <div className="row g-4 pt-5">
            <div className="col-lg-6">
              <div className="ai-card h-100">
                <h4 className="ai-card__title">
                  <i className="icofont-notification"></i>
                  <Trans i18nKey="aiChatbot.notify.alertTitle">
                    {aiNotify.alertTitle}
                  </Trans>
                </h4>
                <ul className="ai-triggers">
                  {aiNotify.triggers.map((trigger) => (
                    <li key={trigger.id}>
                      <span className="ai-triggers__icon">
                        <i className={trigger.icon}></i>
                      </span>
                      <span>
                        <strong>
                          <Trans i18nKey={`aiChatbot.notify.trigger${trigger.id}.title`}>
                            {trigger.title}
                          </Trans>
                        </strong>{" "}
                        <Trans
                          i18nKey={`aiChatbot.notify.trigger${trigger.id}.description`}
                        >
                          {trigger.description}
                        </Trans>
                      </span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
            <div className="col-lg-6">
              <div className="ai-card ai-card--accent h-100">
                <h4 className="ai-card__title">
                  <i className="icofont-handshake-deal"></i>
                  <Trans i18nKey="aiChatbot.notify.handoffTitle">
                    {aiNotify.handoffTitle}
                  </Trans>
                </h4>
                <p>
                  <Trans i18nKey="aiChatbot.notify.handoff">{aiNotify.handoff}</Trans>
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* How It Works 2: Connected to Your AQS System */}
      <section id="integrate" className="about2 section-padding tcm-how ai-integrate">
        <div className="container">
          <div className="row gx-xl-5 gy-5 align-items-center">
            <div className="col-lg-5">
              <SectionTitle
                subtitle={
                  <Trans i18nKey="aiChatbot.integrate.subtitle">
                    {aiIntegrate.subtitle}
                  </Trans>
                }
                title={
                  <Trans i18nKey="aiChatbot.integrate.title">
                    {aiIntegrate.title}
                  </Trans>
                }
              />
              <ItemList
                items={aiIntegrate.items}
                i18nPrefix="aiChatbot.integrate"
                numbered
              />
            </div>
            <div className="col-lg-7">
              <ShotTabs
                screens={aiIntegrate.screens}
                i18nPrefix="aiChatbot.integrate"
                lng={lng}
              />
            </div>
          </div>

          <div className="ai-examples">
            <h4 className="ai-examples__title">
              <Trans i18nKey="aiChatbot.integrate.examplesTitle">
                {aiIntegrate.examplesTitle}
              </Trans>
            </h4>
            <p className="ai-examples__intro">
              <Trans i18nKey="aiChatbot.integrate.examplesDescription">
                {aiIntegrate.examplesDescription}
              </Trans>
            </p>
            <div className="row g-4">
              {aiIntegrate.examples.map((example) => (
                <div key={example.id} className="col-md-4">
                  <div className="ai-phone h-100">
                    <div className="ai-phone__bar">
                      <span className="ai-phone__icon">
                        <i className={example.icon}></i>
                      </span>
                      <Trans i18nKey={`aiChatbot.integrate.example${example.id}.title`}>
                        {example.title}
                      </Trans>
                    </div>
                    <div className="ai-phone__body">
                      <p className="ai-bubble ai-bubble--in">
                        <Trans
                          i18nKey={`aiChatbot.integrate.example${example.id}.question`}
                        >
                          {example.question}
                        </Trans>
                      </p>
                      <p className="ai-bubble ai-bubble--out">
                        <Trans
                          i18nKey={`aiChatbot.integrate.example${example.id}.answer`}
                        >
                          {example.answer}
                        </Trans>
                      </p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
            <p className="ai-examples__caption">
              <Trans i18nKey="aiChatbot.integrate.examplesCaption">
                {aiIntegrate.examplesCaption}
              </Trans>
            </p>
          </div>

          <div className="erp-flow__note ai-privacy">
            <i className="icofont-shield-alt"></i>
            <p>
              <strong>
                <Trans i18nKey="aiChatbot.integrate.privacyTitle">
                  {aiIntegrate.privacyTitle}
                </Trans>
              </strong>{" "}
              <Trans i18nKey="aiChatbot.integrate.privacy">
                {aiIntegrate.privacy}
              </Trans>
            </p>
          </div>
        </div>
      </section>

      {/* How It Works 3: Multi-Language & Voice */}
      <section id="language" className="about2 section-padding bg-one tcm-how ai-language">
        <div className="container">
          <div className="row gx-xl-5 gy-5 align-items-center">
            <div className="col-lg-7 order-2 order-lg-1">
              <ShotTabs
                screens={aiLanguage.screens}
                i18nPrefix="aiChatbot.language"
                lng={lng}
              />
            </div>
            <div className="col-lg-5 order-1 order-lg-2">
              <SectionTitle
                subtitle={
                  <Trans i18nKey="aiChatbot.language.subtitle">
                    {aiLanguage.subtitle}
                  </Trans>
                }
                title={
                  <Trans i18nKey="aiChatbot.language.title">
                    {aiLanguage.title}
                  </Trans>
                }
              />
              <ItemList
                items={aiLanguage.items}
                i18nPrefix="aiChatbot.language"
                numbered
              />
              <div className="ai-stat">
                <span className="ai-stat__number">{aiLanguage.stat}</span>
                <div>
                  <p className="ai-stat__label">
                    <Trans i18nKey="aiChatbot.language.statLabel">
                      {aiLanguage.statLabel}
                    </Trans>
                  </p>
                  <ul className="ai-stat__langs">
                    {aiLanguage.languages.map((language, j) => (
                      <li key={j}>
                        <Trans i18nKey={`aiChatbot.language.lang${j + 1}`}>
                          {language}
                        </Trans>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Your Own AI Assistant */}
      <section id="persona" className="about2 section-padding tcm-how ai-persona">
        <div className="container">
          <div className="row gx-xl-5 gy-5 align-items-center">
            <div className="col-lg-5">
              <SectionTitle
                subtitle={
                  <Trans i18nKey="aiChatbot.persona.subtitle">
                    {aiPersona.subtitle}
                  </Trans>
                }
                title={
                  <Trans i18nKey="aiChatbot.persona.title">{aiPersona.title}</Trans>
                }
              />
              <ItemList items={aiPersona.items} i18nPrefix="aiChatbot.persona" />
            </div>
            <div className="col-lg-7">
              <div className="row g-4">
                {aiPersona.bots.map((bot) => (
                  <div key={bot.id} className="col-sm-6">
                    <div className="ai-bot translateEffect1 h-100">
                      <span className="ai-bot__avatar">
                        <i className="icofont-robot-face"></i>
                      </span>
                      <h4>{bot.name}</h4>
                      <p className="ai-bot__business">
                        <Trans i18nKey={`aiChatbot.persona.bot${bot.id}.business`}>
                          {bot.business}
                        </Trans>
                      </p>
                      <dl className="ai-bot__meta">
                        <dt>
                          <Trans i18nKey="aiChatbot.persona.roleLabel">
                            {aiPersona.roleLabel}
                          </Trans>
                        </dt>
                        <dd>
                          <Trans i18nKey={`aiChatbot.persona.bot${bot.id}.role`}>
                            {bot.role}
                          </Trans>
                        </dd>
                        <dt>
                          <Trans i18nKey="aiChatbot.persona.toneLabel">
                            {aiPersona.toneLabel}
                          </Trans>
                        </dt>
                        <dd>
                          <Trans i18nKey={`aiChatbot.persona.bot${bot.id}.tone`}>
                            {bot.tone}
                          </Trans>
                        </dd>
                      </dl>
                      <p className="ai-bubble ai-bubble--out ai-bot__greeting">
                        <Trans i18nKey={`aiChatbot.persona.bot${bot.id}.greeting`}>
                          {bot.greeting}
                        </Trans>
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Inbox and Promotions Screens */}
      <section id="screens" className="section-padding erp-screens bg-one ai-screens">
        <div className="container">
          <SectionTitle
            subtitle={
              <Trans i18nKey="aiChatbot.screens.subtitle">{aiScreens.subtitle}</Trans>
            }
            title={<Trans i18nKey="aiChatbot.screens.title">{aiScreens.title}</Trans>}
            direction="center"
          />
          <div className="erp-screens__tabs" role="tablist">
            {aiScreens.screens.map((s) => (
              <button
                key={s.id}
                type="button"
                role="tab"
                aria-selected={s.id === screen.id}
                className={`erp-screens__tab ${s.id === screen.id ? "is-active" : ""}`}
                onClick={() => setActiveScreen(s.id)}
              >
                <i className={s.icon}></i>
                <Trans i18nKey={`aiChatbot.screens.screen${s.id}.title`}>
                  {s.title}
                </Trans>
              </button>
            ))}
          </div>
          <div
            className={`erp-screens__frame${screen.narrow ? " ai-frame--narrow" : ""}`}
            role="tabpanel"
          >
            <div className="erp-screens__frame--bar">
              <span></span>
              <span></span>
              <span></span>
            </div>
            <a href={screenSrc} target="_blank" rel="noreferrer">
              <img key={screenSrc} className="img-fluid" src={screenSrc} alt={screen.title} />
            </a>
          </div>
          <div className="erp-screens__caption">
            <p>
              <Trans i18nKey={`aiChatbot.screens.screen${screen.id}.description`}>
                {screen.description}
              </Trans>
            </p>
            <span>
              <i className="icofont-expand"></i>{" "}
              <Trans i18nKey="aiChatbot.screens.enlarge">{aiScreens.enlarge}</Trans>
            </span>
          </div>
        </div>
      </section>

      {/* Pricing Section */}
      <section id="pricing" className="pricing section-padding ai-pricing">
        <div className="container">
          <SectionTitle
            subtitle={
              <Trans i18nKey="aiChatbot.pricing.subtitle">{aiPricing.subtitle}</Trans>
            }
            title={<Trans i18nKey="aiChatbot.pricing.title">{aiPricing.title}</Trans>}
            direction="center"
          />

          <div className="row g-4 pt-4">
            {/* the two Basic plans, with what Basic includes underneath */}
            <div className="col-lg-8">
              <div className="row g-4">
                {aiPricing.plans.map((plan) => (
                  <div key={plan.id} className="col-md-6">
                    <div
                      className={`pricing__item ai-plan translateEffect1 h-100${
                        plan.highlight ? " ai-plan--highlight" : ""
                      }`}
                    >
                      {plan.badge && (
                        <span className="ai-plan__badge">
                          <Trans i18nKey={`aiChatbot.pricing.plan${plan.id}.badge`}>
                            {plan.badge}
                          </Trans>
                        </span>
                      )}
                      <p className="ai-plan__name">
                        <Trans i18nKey={`aiChatbot.pricing.plan${plan.id}.plan`}>
                          {plan.plan}
                        </Trans>
                      </p>
                      <h3 className="ai-plan__price">{plan.price}</h3>
                      <p className="ai-plan__per">
                        <Trans i18nKey={`aiChatbot.pricing.plan${plan.id}.per`}>
                          {plan.per}
                        </Trans>
                      </p>
                      <p className="ai-plan__note">
                        <Trans i18nKey={`aiChatbot.pricing.plan${plan.id}.note`}>
                          {plan.note}
                        </Trans>
                      </p>
                    </div>
                  </div>
                ))}
              </div>

              <div className="ai-includes">
                <h4 className="ai-includes__title">
                  <Trans i18nKey="aiChatbot.pricing.includesTitle">
                    {aiPricing.includesTitle}
                  </Trans>
                </h4>
                <ul>
                  {aiPricing.includes.map((feature, j) => (
                    <li key={j}>
                      <i className="icofont-check"></i>
                      <Trans i18nKey={`aiChatbot.pricing.include${j + 1}`}>
                        {feature}
                      </Trans>
                    </li>
                  ))}
                </ul>
              </div>
            </div>

            <div className="col-lg-4">
              <div className="pricing__item ai-plan ai-plan--integration translateEffect1 h-100">
                <p className="ai-plan__name">
                  <Trans i18nKey="aiChatbot.pricing.integration.plan">
                    {integration.plan}
                  </Trans>
                </p>
                <h3 className="ai-plan__price ai-plan__price--text">
                  <Trans i18nKey="aiChatbot.pricing.integration.price">
                    {integration.price}
                  </Trans>
                </h3>
                <p className="ai-plan__note">
                  <Trans i18nKey="aiChatbot.pricing.integration.note">
                    {integration.note}
                  </Trans>
                </p>
                <ul className="ai-plan__systems">
                  {integration.items.map((item) => (
                    <li key={item.id}>
                      <span>
                        <i className={item.icon}></i>
                      </span>
                      <Trans i18nKey={`aiChatbot.pricing.integration.item${item.id}`}>
                        {item.title}
                      </Trans>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </div>

          <div className="ai-pricing__contact">
            <ContactButtons hero={aiHero} i18nPrefix="aiChatbot.pricing" />
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <Ctav3 className="ai-cta" />

      <Footer2 />
    </>
  );
};

export default AiChatbot;
